import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import InputItem from './InputItem';
import { uploadTemporaryFile, markFileAsUsed } from '../api/storage/actions';
import useUXStore from '../api/ux/store';

interface ImageUploadInputProps {
  id: string;
  text: string;
  caption?: string;
  value?: string; // Initial/current image URL
  permanentPath?: string; // Path for permanent storage
  inputSize?: number;
  edit?: boolean;
  onChanged?: (id: string, url: string) => void;
  acceptedTypes?: string;
  maxSizeMB?: number;
}

export interface ImageUploadInputRef {
  apply: () => Promise<string | null>; // Returns permanent URL or null if no temp file
  hasTemporaryFile: () => boolean;
}

const ImageUploadInput = forwardRef<ImageUploadInputRef, ImageUploadInputProps>(({
  id,
  text,
  caption = '',
  value = '',
  permanentPath = 'images',
  inputSize = 4,
  edit = true,
  onChanged,
  acceptedTypes = 'image/jpeg,image/png,image/gif',
  maxSizeMB = 5,
}, ref) => {
  const [loading, setLoading] = useState(false);
  const [currentUrl, setCurrentUrl] = useState(value);
  const [tempUrl, setTempUrl] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const { addToast } = useUXStore();

  useEffect(() => {
    // Update current URL when value prop changes (e.g., initial load)
    if (value && !tempUrl && !localPreview) {
      setCurrentUrl(value);
    }
  }, [value, tempUrl, localPreview]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    apply: async () => {
      if (!tempUrl) return null;

      setLoading(true);
      try {
        const response = await markFileAsUsed(tempUrl, permanentPath);
        const permanentUrl = response.permanent_url;
        
        // Update state
        setCurrentUrl(permanentUrl);
        setTempUrl(null);
        setLocalPreview(null);

        // Notify parent
        if (onChanged) {
          onChanged(id, permanentUrl);
        }

        // addToast({
        //   type: 'success',
        //   message: 'Image saved permanently',
        // });

        return permanentUrl;
      } catch (error: any) {
        console.error('Mark as used error:', error);
        addToast(
          'error',
          error.message || 'Failed to save image permanently',
        );
        throw error;
      } finally {
        setLoading(false);
      }
    },
    hasTemporaryFile: () => {
      return tempUrl !== null;
    }
  }));

  const handleImageChanged = async (file: File) => {
    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      addToast('error',
        `File size exceeds ${maxSizeMB}MB limit`,
      );
      return;
    }

    // Validate file type
    const fileType = file.type;
    const acceptedTypesList = acceptedTypes.split(',').map(t => t.trim());
    if (!acceptedTypesList.includes(fileType)) {
      addToast('error','Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }

    setLoading(true);

    try {
      // Create local preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        const preview = e.target?.result as string;
        setLocalPreview(preview);
        setCurrentUrl(preview); // Show preview immediately
      };
      reader.readAsDataURL(file);

      // Upload to temporary storage
      const uploadResponse = await uploadTemporaryFile(file);
      setTempUrl(uploadResponse.url);
      
      // Update current URL to temp URL (but keep showing local preview for performance)
      // The actual temp URL will be used when marking as permanent
      
      // Notify parent of temporary URL
      if (onChanged) {
        onChanged(id, uploadResponse.url);
      }

      // addToast({
      //   type: 'info',
      //   message: 'Image uploaded. Changes will be saved when you apply.',
      // });
    } catch (error: any) {
      console.error('Upload error:', error);
      addToast( 'error', error.message || 'Failed to upload image');
      // Reset on error
      setCurrentUrl(value);
      setLocalPreview(null);
      setTempUrl(null);
    } finally {
      setLoading(false);
    }
  };

  // Use the current URL for display (could be original, local preview, or permanent)
  const displayUrl = localPreview || currentUrl;

  return (
    <InputItem
      id={id}
      text={text}
      caption={caption}
      value={displayUrl}
      edit={edit}
      isImage={true}
      inputSize={inputSize}
      loading={loading}
      onImageChanged={edit ? handleImageChanged : undefined}
      onChanged={onChanged}
    />
  );
});

ImageUploadInput.displayName = 'ImageUploadInput';

export default ImageUploadInput;